/**
 * Lot Number Validator & Auto-Correct Utility
 * 
 * Format yang benar:
 * 1. Warehouse: {Blok}{Posisi}.{nomor_lot}
 *    - Blok: A - O
 *    - Posisi: G, X, Y, Z
 *    - nomor_lot: 1 - 34
 *    - Contoh: AG.1, AX.20, BZ.12, OY.34
 * 
 * 2. Chiller: CL.{nomor}
 *    - nomor: 1 - 10
 *    - Contoh: CL.1, CL.10
 */

export const VALID_BLOK = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O'];
export const VALID_POSISI = ['G', 'X', 'Y', 'Z'];
export const MIN_LOT = 1;
export const MAX_LOT = 34;
export const MAX_CL_LOT = 10;

/**
 * Mencoba auto-correct format Nomor Lot yang salah
 * Mendukung format Warehouse (AG.1) dan Chiller (CL.1)
 * @param {string} input - Input user
 * @returns {{ corrected: string, isValid: boolean, warning: string | null }}
 */
export const autoCorrectLotNumber = (input) => {
    if (!input || typeof input !== 'string') {
        return { corrected: '', isValid: false, warning: null };
    }

    // Uppercase dan hapus spasi
    let cleaned = input.toUpperCase().trim();

    // Hapus semua spasi di tengah input
    cleaned = cleaned.replace(/\s+/g, '');

    // Hapus semua karakter selain A-Z dan 0-9 dan titik
    cleaned = cleaned.replace(/[^A-Z0-9.]/g, '');

    // ——— Chiller (CL) check ———
    // Pola CL: CL.{nomor} atau CL{nomor}
    const patternCL = /^CL\.?(\d+)$/;
    const clMatch = cleaned.match(patternCL);
    if (clMatch) {
        const clNum = parseInt(clMatch[1], 10);
        if (clNum >= 1 && clNum <= MAX_CL_LOT) {
            return { corrected: `CL.${clNum}`, isValid: true, warning: null };
        } else {
            return {
                corrected: cleaned,
                isValid: false,
                warning: `Nomor Chiller "${clNum}" tidak valid (harus 1-${MAX_CL_LOT})`,
            };
        }
    }

    // ——— Warehouse (standard) check ———
    let blok = '';
    let posisi = '';
    let nomorLot = '';

    // Pola 1: Format benar {BlokPosisi}.{Nomor} - contoh: AG.1
    const patternCorrect = /^([A-O])([GXYZ])\.(\d+)$/;
    // Pola 2: Tanpa titik {BlokPosisiNomor} - contoh: AX1, AG20
    const patternNoSeparator = /^([A-O])([GXYZ])(\d+)$/;
    // Pola 3: Titik di tempat salah {Blok}.{Posisi}.{Nomor} - contoh: A.X.1
    const patternExtraDots = /^([A-O])\.([GXYZ])\.(\d+)$/;
    // Pola 4: Titik setelah Blok {Blok}.{PosisiNomor} - contoh: A.X1
    const patternDotAfterBlok = /^([A-O])\.([GXYZ])(\d+)$/;
    // Pola 5: Posisi dan Blok terbalik {Posisi}{Blok}.{Nomor} - contoh: GA.1
    const patternReversed = /^([GXYZ])([A-O])\.(\d+)$/;

    let match;

    if ((match = cleaned.match(patternCorrect))) {
        blok = match[1];
        posisi = match[2];
        nomorLot = match[3];
    } else if ((match = cleaned.match(patternNoSeparator))) {
        blok = match[1];
        posisi = match[2];
        nomorLot = match[3];
    } else if ((match = cleaned.match(patternExtraDots))) {
        blok = match[1];
        posisi = match[2];
        nomorLot = match[3];
    } else if ((match = cleaned.match(patternDotAfterBlok))) {
        blok = match[1];
        posisi = match[2];
        nomorLot = match[3];
    } else if ((match = cleaned.match(patternReversed))) {
        // Swap posisi dan blok
        posisi = match[1];
        blok = match[2];
        nomorLot = match[3];
    } else {
        // Coba ekstrak komponen secara manual
        const letters = cleaned.replace(/[^A-Z]/g, '');
        const numbers = cleaned.replace(/[^0-9]/g, '');

        if (letters.length >= 2) {
            const char1 = letters[0];
            const char2 = letters[1];

            if (VALID_BLOK.includes(char1) && VALID_POSISI.includes(char2)) {
                blok = char1;
                posisi = char2;
            } else if (VALID_POSISI.includes(char1) && VALID_BLOK.includes(char2)) {
                // Terbalik
                blok = char2;
                posisi = char1;
            }
        }
        nomorLot = numbers;
    }

    // Validasi komponen
    const warnings = [];

    if (!VALID_BLOK.includes(blok)) {
        warnings.push(`Blok "${blok || '?'}" tidak valid (harus A-O)`);
    }

    if (!VALID_POSISI.includes(posisi)) {
        warnings.push(`Posisi "${posisi || '?'}" tidak valid (harus G, X, Y, atau Z)`);
    }

    const lotNum = parseInt(nomorLot, 10);
    if (isNaN(lotNum) || lotNum < MIN_LOT || lotNum > MAX_LOT) {
        warnings.push(`Nomor Lot "${nomorLot || '?'}" tidak valid (harus 1-34)`);
    }

    const isValid = warnings.length === 0;
    const corrected = isValid ? `${blok}${posisi}.${lotNum}` : cleaned;
    const warning = warnings.length > 0 ? warnings.join(', ') : null;

    return { corrected, isValid, warning };
};

/**
 * Validasi apakah Nomor Lot sudah dalam format yang benar
 * @param {string} value - Nilai yang akan divalidasi
 * @returns {{ isValid: boolean, warning: string | null }}
 */
export const validateLotNumber = (value) => {
    if (!value) return { isValid: true, warning: null }; // Empty is ok (not required)

    const result = autoCorrectLotNumber(value);
    return { isValid: result.isValid, warning: result.warning };
};

/**
 * Format helper untuk menampilkan contoh format yang benar
 */
export const LOT_NUMBER_FORMAT = {
    pattern: '{Blok}{Posisi}.{Nomor} atau CL.{Nomor}',
    example: 'AG.1, BX.20, CL.5',
    tooltip: 'Format: {Blok}{Posisi}.{Nomor} (cth: AG.1) atau CL.{Nomor} (cth: CL.5)',
    placeholder: 'Nomor Lot (cth: AG.1, CL.5)',
};
